import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  CanAccess,
  EditBase,
  Form,
  required,
  useGetList,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { TextArrayInput } from "@/components/admin/text-array-input";
import { TextInput } from "@/components/admin/text-input";

import { FormToolbar } from "../layout/FormToolbar";
import type { DealStageConfig } from "../types";
import { StageActivitiesSection } from "./StageActivitiesSection";
import { StageArchiveActions } from "./StageArchiveActions";
import { StageCriteriaSection } from "./StageCriteriaSection";
import {
  MAX_REQUIRED_ACTIVITIES,
  STAGE_LABEL_MAX_LENGTH,
  toStageConfigErrorMessage,
  validateRequiredActivities,
  validateStageLabel,
} from "./stageConfigForm";
import { TargetTimeInput } from "./TargetTimeInput";

/**
 * Almikia Epic EP (TASK-105): a single stage's configuration -- direccion
 * relabels it, sets its target time, maintains its placeholder
 * "Actividades de la etapa" list, toggles its advance criteria and, for a
 * non-system stage, archives/reactivates it. `key`/`placement`/`is_system`
 * are never editable here (immutable after insert, DATA CONTRACT EP (1)).
 * Gated the same way as the list/create screens so a non-direccion role
 * can't reach an editable form by typing the URL directly. Epic EA
 * (TASK-115) adds `StageActivitiesSection` right below the free-text list,
 * additively linking catalogue activities (`deal_stage_activities`) to the
 * same "Actividades de la etapa" section without touching the free-text
 * list's own validation/behavior.
 */
export const DealStageConfigEdit = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  return (
    <CanAccess
      resource="deal_stage_configs"
      action="edit"
      accessDenied={<Navigate to="/" replace />}
    >
      <EditBase
        redirect="list"
        transform={(data) => ({ ...data, label: data.label.trim() })}
        mutationOptions={{
          onSettled: () => {
            // Almikia Epic EP: see DealStageConfigCreate.tsx -- a relabel
            // must also bust the effective-stages cache read by
            // `useConfigurationLoader`, which react-admin's default
            // `[resource, "getList"]` invalidation never touches.
            // Uses `onSettled` (not `onSuccess`) because EditBase's own
            // `onSuccess` drives the undoable notification/redirect --
            // overriding it there would suppress the real dataProvider
            // write for the undoable mutation mode.
            queryClient.invalidateQueries({
              queryKey: ["deal_stage_configs", "effective"],
            });
          },
          onError: (error) =>
            notify(toStageConfigErrorMessage(error), { type: "error" }),
        }}
      >
        <DealStageConfigEditForm />
      </EditBase>
    </CanAccess>
  );
};

const DealStageConfigEditForm = () => {
  const translate = useTranslate();
  const record = useRecordContext<DealStageConfig>();
  const { data: otherConfigs } = useGetList<DealStageConfig>(
    "deal_stage_configs",
    { pagination: { page: 1, perPage: 100 } },
  );
  // Epic EA (TASK-115): blocks the main "Guardar" while
  // `StageActivitiesSection` holds unsaved link picks -- otherwise the main
  // submit's success notice/redirect silently discards them.
  const [hasPendingLinks, setHasPendingLinks] = useState(false);

  if (!record || !otherConfigs) return null;

  const activeLabels = otherConfigs
    .filter((config) => config.id !== record.id && config.archived_at == null)
    .map((config) => config.label);

  return (
    <div className="mt-2 max-w-lg mx-auto flex flex-col gap-4 pb-8">
      <Card>
        <CardContent>
          <Form className="space-y-4 pt-4">
            <TextInput
              source="key"
              label="resources.deal_stage_configs.fields.key"
              readOnly
              helperText="resources.deal_stage_configs.fields.key_help"
            />
            <TextInput
              source="label"
              label="resources.deal_stage_configs.fields.label"
              validate={[
                required(),
                (value: string) => validateStageLabel(value, activeLabels),
              ]}
              maxLength={STAGE_LABEL_MAX_LENGTH}
              helperText={false}
            />

            <Separator />

            <TargetTimeInput source="target_hours" />

            <Separator />

            <TextArrayInput
              source="required_activities"
              label="resources.deal_stage_configs.fields.required_activities"
              placeholder={translate(
                "resources.deal_stage_configs.required_activities.placeholder",
              )}
              helperText="resources.deal_stage_configs.required_activities.help"
              validate={validateRequiredActivities}
            />
            <p className="text-xs text-muted-foreground -mt-2">
              {translate(
                "resources.deal_stage_configs.required_activities.limit",
                { max: MAX_REQUIRED_ACTIVITIES },
              )}
            </p>

            <Separator />

            <StageActivitiesSection
              stageKey={record.key}
              onDirtyChange={setHasPendingLinks}
            />
            {hasPendingLinks ? (
              <p className="text-xs text-destructive" role="alert">
                {translate(
                  "resources.deal_stage_configs.activities.unsaved_links_warning",
                )}
              </p>
            ) : null}

            <Separator />

            <FormToolbar saveDisabled={hasPendingLinks} />
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <StageCriteriaSection stageKey={record.key} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            {translate("resources.deal_stage_configs.alerts.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {translate("resources.deal_stage_configs.alerts.empty")}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <StageArchiveActions record={record} />
        </CardContent>
      </Card>
    </div>
  );
};
